/**
 * Карточка-контейнер с необязательным заголовком и состоянием.
 *
 * Состояние — не оформление, а сообщение (ТЗ UI-26). Обычная карточка ни на
 * что не отзывается: ложный отклик на наведение обещает нажатие, которого
 * нет. Выбранная, недоступная и ошибочная различаются не цветом, а знаком и
 * подписью — иначе они неразличимы для тех, кто видит их серыми.
 */
import type { ReactNode } from 'react';

export type CardState = 'static' | 'interactive' | 'selected' | 'disabled' | 'error';

/** Знак состояния: он и есть то, что читается без цвета. */
const MARK: Partial<Record<CardState, string>> = {
  selected: '✓',
  disabled: '🔒',
  error: '!',
};

export interface CardProps {
  /**
   * Заголовок карточки; `undefined` — карточка без него.
   *
   * `undefined` разрешён явно: вызывающий решает, показывать заголовок, уже
   * при вычислении значения, и подставлять его условным спредом было бы
   * дороже читателю, чем этому типу.
   */
  readonly title?: string | undefined;
  /** Состояние карточки; по умолчанию обычная. */
  readonly state?: CardState;
  /**
   * Подпись к состоянию: почему недоступна, что за ошибка, что выбрано.
   *
   * Для `disabled` и `error` она и есть содержание состояния: карточка,
   * которая просто серая, не отвечает на вопрос «почему».
   */
  readonly note?: string | undefined;
  readonly children: ReactNode;
}

export function Card({ title, state = 'static', note, children }: CardProps): React.JSX.Element {
  const mark = MARK[state];

  return (
    <section
      className={`t2x-card t2x-card--${state}`}
      aria-disabled={state === 'disabled' ? true : undefined}
    >
      {title !== undefined && (
        <h2 className="t2x-card__title">
          {mark !== undefined && (
            <span className="t2x-card__mark" aria-hidden="true">
              {mark}
            </span>
          )}
          {title}
        </h2>
      )}
      {note !== undefined && (
        <p className="t2x-card__note" role={state === 'error' ? 'alert' : undefined}>
          {note}
        </p>
      )}
      {children}
    </section>
  );
}
